import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Search, ArrowRight } from 'lucide-react';

interface UOHeatmapRow {
  uo_id: string;
  uo_nome: string;
  cluster_nome: string;
  contract_id: string;
  contrato_nome: string;
  score_risco_uo: number | null;
  nivel_calor: string;
  total_nao_conformidades: number;
  total_riscos_imediatos: number;
  progresso: number;
  cidade: string | null;
}

interface RiskUORankingProps {
  data: UOHeatmapRow[];
  search: string;
  onSearchChange: (v: string) => void;
  selectedUOId: string | null;
  onSelectUO: (uoId: string | null) => void;
}

const CALOR_BG: Record<string, string> = {
  critico: 'bg-destructive/10 border-destructive/30',
  alto: 'bg-orange-500/10 border-orange-500/30',
  moderado: 'bg-yellow-500/10 border-yellow-500/30',
  baixo: 'bg-green-500/10 border-green-500/30',
};

const CALOR_TEXT: Record<string, string> = {
  critico: 'text-destructive',
  alto: 'text-orange-500',
  moderado: 'text-yellow-600',
  baixo: 'text-green-600',
};

const RiskUORanking: React.FC<RiskUORankingProps> = ({
  data, search, onSearchChange, selectedUOId, onSelectUO,
}) => {
  const navigate = useNavigate();

  const filtered = React.useMemo(() => {
    if (!search) return data;
    const q = search.toLowerCase();
    return data.filter(r =>
      r.uo_nome.toLowerCase().includes(q) ||
      r.contrato_nome.toLowerCase().includes(q) ||
      r.cluster_nome.toLowerCase().includes(q) ||
      (r.cidade?.toLowerCase().includes(q) ?? false)
    );
  }, [data, search]);

  return (
    <div>
      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar UO, contrato, cluster ou cidade..."
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-muted-foreground font-medium">UO</th>
              <th className="text-left py-3 px-4 text-muted-foreground font-medium">Cidade</th>
              <th className="text-left py-3 px-4 text-muted-foreground font-medium">Contrato</th>
              <th className="text-center py-3 px-4 text-muted-foreground font-medium">Nível</th>
              <th className="text-center py-3 px-4 text-muted-foreground font-medium">Score</th>
              <th className="text-center py-3 px-4 text-muted-foreground font-medium">NC</th>
              <th className="text-center py-3 px-4 text-muted-foreground font-medium">Riscos Imediatos</th>
              <th className="text-center py-3 px-4 text-muted-foreground font-medium">Progresso</th>
              <th className="text-center py-3 px-4 text-muted-foreground font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">Nenhuma UO encontrada</td></tr>
            ) : filtered.map(row => {
              const isSelected = row.uo_id === selectedUOId;
              return (
                <tr
                  key={row.uo_id}
                  data-uo-id={row.uo_id}
                  className={`border-b border-border/50 hover:bg-muted/50 transition-colors cursor-pointer ${isSelected ? 'bg-primary/5 ring-1 ring-primary/20' : ''}`}
                  onClick={() => onSelectUO(row.uo_id)}
                >
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium truncate max-w-[200px]">{row.uo_nome}</p>
                      <p className="text-xs text-muted-foreground">{row.cluster_nome}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-xs">{row.cidade ?? '—'}</td>
                  <td className="py-3 px-4 text-xs truncate max-w-[160px]">{row.contrato_nome}</td>
                  <td className="py-3 px-4 text-center">
                    <Badge variant="outline" className={`${CALOR_BG[row.nivel_calor]} ${CALOR_TEXT[row.nivel_calor]}`}>
                      {row.nivel_calor}
                    </Badge>
                  </td>
                  <td className={`py-3 px-4 text-center font-bold ${CALOR_TEXT[row.nivel_calor]}`}>
                    {row.score_risco_uo ?? '—'}
                  </td>
                  <td className="py-3 px-4 text-center">{row.total_nao_conformidades}</td>
                  <td className="py-3 px-4 text-center">{row.total_riscos_imediatos}</td>
                  <td className="py-3 px-4 text-center">{row.progresso}%</td>
                  <td className="py-3 px-4 text-center">
                    <ArrowRight className="h-4 w-4 text-muted-foreground mx-auto" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RiskUORanking;
